import React, { useState } from 'react';
import { toast } from 'react-toastify';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  keys,
  useRemoveSitrepV2,
} from 'components/DisasterGPT/hooks/useSitrepsV2';
import RequestApprovalConfirmationModal from 'components/Reports/RequestApprovalConfirmationModal';
import RequestApprovalSuccessModal from 'components/Reports/RequestApprovalSuccessModal';
import {
  LAYOUT_OPTIONS,
  type SitrepLayoutMode,
} from 'components/Reports/SitrepLayoutSwitcher';
import type { GetSitrepsResponse } from 'components/Reports/reports.types';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { ITEM_TYPES } from 'constants/itemType';
import { useUserItemId } from 'components/IAP/hooks/commonHooks';
import { useGetPositionsForIncidentId } from 'hooks/incident/useRoster';
import {
  useEligibleApprovalReviewers,
  useRequestApprovals,
} from 'hooks/useApprovals';
import Sentry from '../../instrument';

const POSITION_NAMES_FOR_INCIDENT_REPORT_APPROVAL = [
  'Situation Unit Leader',
  'Planning Section Chief',
  'Incident Commander',
];

type SitrepDraftActionsMenuProps = {
  sitrep: GetSitrepsResponse;
  layoutMode: SitrepLayoutMode;
  onLayoutChange: (mode: SitrepLayoutMode) => void;
  /**
   * When true the draft-only groups (add section, submit for review, delete
   * draft) are shown. Layout switching is always available.
   */
  canManageDraft: boolean;
  onAddSection: () => void;
  isAddingSection?: boolean;
  onAddFiles: () => void;
  isAddFilesDisabled?: boolean;
};

/**
 * Overflow menu shown in the SITREP card header. Groups the per-report layout
 * choice, section authoring, and the draft lifecycle actions (submit for
 * review, delete draft) behind a single vertical-ellipsis toggle.
 */
const SitrepDraftActionsMenu: React.FC<SitrepDraftActionsMenuProps> = ({
  sitrep,
  layoutMode,
  onLayoutChange,
  canManageDraft,
  onAddSection,
  isAddingSection = false,
  onAddFiles,
  isAddFilesDisabled = false,
}) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [pendingSuccess, setPendingSuccess] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const userId = useUserItemId();
  const requestApprovals = useRequestApprovals(keys.base, {
    suppressSuccessToast: true,
    deferQueryInvalidation: true,
  });
  const removeSitrep = useRemoveSitrepV2();
  const aorEligibleReviewersQuery = useEligibleApprovalReviewers({
    itemId: sitrep.item_id,
    enabled: !sitrep.incident_id,
  });
  const incidentPositionsQuery = useGetPositionsForIncidentId(
    sitrep.incident_id || ''
  );
  const eligibleAorReviewers = aorEligibleReviewersQuery.data ?? [];
  const otherAorReviewers = eligibleAorReviewers.filter(
    (r) => r.reviewerItemId !== userId
  );
  const selfAorReviewer = eligibleAorReviewers.find(
    (r) => r.reviewerItemId === userId
  );
  // When the drafter is the only eligible reviewer in the AOR, let them
  // submit to themselves so they can review and approve their own draft.
  const aorReviewers =
    otherAorReviewers.length === 0 && selfAorReviewer
      ? [selfAorReviewer]
      : otherAorReviewers;
  const isSelfReview =
    aorReviewers.length === 1 && aorReviewers[0] === selfAorReviewer;

  const handleConfirmApproval = async () => {
    let reviewers: { reviewerItemId: string }[];

    if (sitrep.incident_id) {
      const positions = incidentPositionsQuery.data;
      reviewers = POSITION_NAMES_FOR_INCIDENT_REPORT_APPROVAL.map(
        (positionName) => {
          const position = positions?.find((p) => p.name === positionName);
          if (!position) {
            return null;
          }
          return { reviewerItemId: position.item_id };
        }
      ).filter((r): r is { reviewerItemId: string } => r !== null);
    } else {
      reviewers = aorReviewers.map((r) => ({
        reviewerItemId: r.reviewerItemId,
      }));

      if (reviewers.length === 0) {
        toast.error('No eligible reviewers found for this report.');
        setShowConfirmation(false);
        return;
      }
    }

    await requestApprovals.mutateAsync({
      itemId: sitrep.item_id,
      itemType: ITEM_TYPES.Sitrep,
      reviewers,
      approvalsRequiredInOrder: !!sitrep.incident_id,
      groupId: sitrep.group_guid,
      incidentId: sitrep.incident_id || undefined,
    });
    setPendingSuccess(true);
    setShowConfirmation(false);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteConfirmation(false);
    try {
      await removeSitrep.mutateAsync({ sitrepId: sitrep.id });
      toast.success('Draft deleted');
    } catch (error) {
      Sentry.captureException(error);
      toast.error('Failed to delete draft');
    }
  };

  return (
    <span
      role="presentation"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="outline"
              size="icon-sm"
              aria-label="Report actions"
              title="Report actions"
            />
          }
        >
          <SharedIcon iconName="more_vert" size="sm" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuRadioGroup
            value={layoutMode}
            onValueChange={(value: SitrepLayoutMode) => onLayoutChange(value)}
          >
            <DropdownMenuLabel className="text-weak">Layout</DropdownMenuLabel>
            {LAYOUT_OPTIONS.map((option) => (
              <DropdownMenuRadioItem
                key={option.value}
                value={option.value}
                closeOnClick
              >
                {option.label}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
          {canManageDraft && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={isAddingSection}
                onClick={onAddSection}
              >
                <SharedIcon iconName="add" size="sm" />
                Add Section
              </DropdownMenuItem>
              {isAddFilesDisabled ? (
                <Tooltip>
                  <TooltipTrigger render={<span />}>
                    <DropdownMenuItem disabled onClick={onAddFiles}>
                      <SharedIcon iconName="attach_file" size="sm" />
                      Add Files
                    </DropdownMenuItem>
                  </TooltipTrigger>
                  <TooltipContent side="left">
                    Link this report to an incident to attach files
                  </TooltipContent>
                </Tooltip>
              ) : (
                <DropdownMenuItem onClick={onAddFiles}>
                  <SharedIcon iconName="attach_file" size="sm" />
                  Add Files
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={requestApprovals.isPending}
                onClick={() => setShowConfirmation(true)}
              >
                <SharedIcon iconName="send" size="sm" />
                Submit for review
              </DropdownMenuItem>
              <DropdownMenuItem
                variant="destructive"
                disabled={removeSitrep.isPending}
                onClick={() => setShowDeleteConfirmation(true)}
              >
                <SharedIcon iconName="delete" size="sm" />
                Delete draft
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <RequestApprovalConfirmationModal
        show={showConfirmation}
        onHide={() => setShowConfirmation(false)}
        onConfirm={handleConfirmApproval}
        isSubmitting={requestApprovals.isPending}
        name={sitrep.name}
        createdBy={sitrep.created_by_user_name ?? 'Unknown'}
        createdAt={sitrep.timestamp}
        editedBy={
          sitrep.updated_by_user_name ??
          sitrep.created_by_user_name ??
          'Unknown'
        }
        editedAt={sitrep.updated_at ?? sitrep.timestamp}
        incidentId={sitrep.incident_id}
        aorReviewers={aorReviewers}
        isSelfReview={isSelfReview}
        positionNames={
          sitrep.incident_id
            ? POSITION_NAMES_FOR_INCIDENT_REPORT_APPROVAL
            : aorReviewers.map((r) => r.displayName)
        }
        fallbackReviewerLabel={
          sitrep.incident_id
            ? undefined
            : 'No SITREP reviewers with AOR permissions are configured for this report.'
        }
        onExited={() => {
          if (pendingSuccess) {
            setPendingSuccess(false);
            setShowSuccess(true);
          }
        }}
      />
      <RequestApprovalSuccessModal
        show={showSuccess}
        draftName={sitrep.name}
        editedBy={
          sitrep.updated_by_user_name ??
          sitrep.created_by_user_name ??
          'Unknown'
        }
        incidentId={sitrep.incident_id}
        onHide={() => {
          setShowSuccess(false);
          requestApprovals.invalidateQueries();
        }}
      />
      <AlertDialog
        open={showDeleteConfirmation}
        onOpenChange={(open) => {
          if (!open) setShowDeleteConfirmation(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete draft</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {sitrep.name}? This removes the
              draft and its sections from Reports. This action cannot be undone
              from the UI.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => void handleConfirmDelete()}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </span>
  );
};

export default SitrepDraftActionsMenu;
